/**
 * PDF 文字层吸附：用 pdf.js 抽字框校准 AI 返回的大致 bbox。
 * 多命中优先级：同页 > 精确匹配 > 距后端 bbox 更近 > 文本特异性（目标/碎片长度比）。
 * 连续碎片：精确整句相等；或以目标为前缀（词界后可有 CAP 等尾巴）时按字符区间映射框，
 * 避免把尾部一并外包，也避免页顶长文「中间包含」目标时吞成巨型框。
 * 同值多字段（如税号=增值税号）优先用精确命中按 y 一一对应；仅一处精确时各字段共用该框。
 */

/** 页内文字碎片（左上原点、与 pdf.js scale=1 viewport/画布一致，已含 crop 偏移）。 */
export interface PdfTextFragment {
    /** 页码，从 1 起。 */
    page: number;
    /** 碎片原文。 */
    text: string;
    /** 距页左边缘。 */
    x: number;
    /** 距页顶边缘。 */
    y: number;
    /** 宽度。 */
    w: number;
    /** 高度。 */
    h: number;
}

/** 后端给出的对位提示，用于多命中消歧。 */
export interface PdfSnapHint {
    /** 后端声明的页码。 */
    page: number;
    /** 后端大致 bbox；可为 null。 */
    bbox: { x: number; y: number; w: number; h: number } | null;
}

/** 吸附得到的页内矩形。 */
export interface PdfSnappedBox {
    /** 实际命中页码。 */
    page: number;
    /** 左上原点矩形。 */
    bbox: { x: number; y: number; w: number; h: number };
}

/** 命中候选，带匹配档位便于排序。 */
export interface PdfSnapCandidate {
    /** 命中页。 */
    page: number;
    /** 矩形。 */
    bbox: { x: number; y: number; w: number; h: number };
    /** 是否整段精确匹配。 */
    exact: boolean;
    /** 命中处规范化文本长度，用于特异性 = 目标长 / 此长度。 */
    haystackLength: number;
}

/**
 * 规范化比对用文本：压空白、大写。
 * @param value 原文
 */
export function normalizePdfSnapText(value: string): string {
    return `${value || ''}`.replace(/\s+/g, ' ').trim().toUpperCase();
}

/**
 * 矩形中心点。
 * @param box 矩形
 */
function boxCenter(box: { x: number; y: number; w: number; h: number }): { x: number; y: number } {
    return { x: box.x + box.w / 2, y: box.y + box.h / 2 };
}

/**
 * 两中心欧氏距离平方。
 * @param a 中心 A
 * @param b 中心 B
 */
function centerDistanceSq(
    a: { x: number; y: number },
    b: { x: number; y: number },
): number {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return dx * dx + dy * dy;
}

/**
 * 目标相对 haystack 的特异性（越大越贴字，避免吸到长页眉）。
 * @param targetLen 目标长度
 * @param haystackLen 命中文本长度
 */
export function pdfSnapSpecificity(targetLen: number, haystackLen: number): number {
    if (targetLen <= 0 || haystackLen <= 0) {
        return 0;
    }
    return targetLen / haystackLen;
}

/**
 * 按字符比例估算子串在碎片内的横向框。
 * @param fragment 碎片
 * @param target 已规范化目标
 * @param startIndex 目标在规范化碎片文本中的起点
 */
function ratioBoxInFragment(
    fragment: PdfTextFragment,
    target: string,
    startIndex: number,
): { x: number; y: number; w: number; h: number } {
    /** 规范化碎片文本长度。 */
    const textLen = Math.max(normalizePdfSnapText(fragment.text).length, 1);
    /** 横向起点比例。 */
    const ratioStart = startIndex / textLen;
    /** 横向宽度比例。 */
    const ratioWidth = target.length / textLen;
    return {
        x: fragment.x + fragment.w * ratioStart,
        y: fragment.y,
        w: Math.max(fragment.w * ratioWidth, fragment.h * 0.6),
        h: fragment.h,
    };
}

/**
 * 在长串中按词界/括号定位短码，估算子串对应的横向框。
 * @param fragment 碎片
 * @param target 已规范化的短码
 */
function tokenBoxInFragment(
    fragment: PdfTextFragment,
    target: string,
): { x: number; y: number; w: number; h: number } | null {
    /** 规范化碎片文本。 */
    const text = normalizePdfSnapText(fragment.text);
    /** 词界或括号内的短码。 */
    const pattern = new RegExp(`(?:^|[^A-Z0-9])(${target.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})(?:[^A-Z0-9]|$)`);
    const matched = text.match(pattern);
    if (!matched || matched.index == null) {
        return null;
    }
    /** 捕获组在整串中的起点（跳过前导分隔符）。 */
    const groupStart = matched[0].startsWith(target)
        ? matched.index
        : matched.index + 1;
    return ratioBoxInFragment(fragment, target, groupStart);
}

/**
 * 判断吸附框相对识别值是否大得不合理（如整行说明文字）。
 * @param bbox 候选框
 * @param value 识别值
 */
export function isImplausiblePdfSnapBox(
    bbox: { w: number; h: number },
    value: string,
): boolean {
    /** 规范化目标长度。 */
    const targetLen = normalizePdfSnapText(value).length;
    if (targetLen <= 0) {
        return true;
    }
    /** 按字数估算合理宽度上限（含余量）。 */
    const maxWidth = Math.max(targetLen * 8, 40) * 4;
    /** 字高上限（含跨行地址外包）。 */
    const maxHeight = 48;
    return bbox.w > maxWidth || bbox.h > maxHeight;
}

/**
 * 合并两个左上原点矩形为外包框。
 * @param a 矩形 A
 * @param b 矩形 B
 */
function unionBBox(
    a: { x: number; y: number; w: number; h: number },
    b: { x: number; y: number; w: number; h: number },
): { x: number; y: number; w: number; h: number } {
    /** 左缘。 */
    const x = Math.min(a.x, b.x);
    /** 顶缘。 */
    const y = Math.min(a.y, b.y);
    /** 右缘。 */
    const right = Math.max(a.x + a.w, b.x + b.w);
    /** 底缘。 */
    const bottom = Math.max(a.y + a.h, b.y + b.h);
    return { x, y, w: right - x, h: bottom - y };
}

/**
 * 将同页连续碎片按单空格拼接后的规范化串中某一字符区间，映射为外包框。
 * 部分落在碎片内时按字符比例裁横向框（如第二行只要 STAZIONE 4、不要 CAP）。
 * @param slice 连续同页碎片（文档顺序）
 * @param rangeStart 规范化拼接串中的起点
 * @param rangeLength 区间长度
 */
function boxForNormalizedJoinRange(
    slice: PdfTextFragment[],
    rangeStart: number,
    rangeLength: number,
): { x: number; y: number; w: number; h: number } | null {
    if (!slice.length || rangeLength <= 0) {
        return null;
    }
    /** 各碎片在拼接串中的 [start, end)。 */
    const segments: Array<{
        frag: PdfTextFragment;
        text: string;
        start: number;
        end: number;
    }> = [];
    /** 拼接串游标。 */
    let cursor = 0;
    for (let i = 0; i < slice.length; i += 1) {
        /** 碎片规范化文本。 */
        const text = normalizePdfSnapText(slice[i].text);
        if (!text) {
            continue;
        }
        if (segments.length > 0) {
            cursor += 1;
        }
        /** 本段起点。 */
        const start = cursor;
        /** 本段终点。 */
        const end = cursor + text.length;
        segments.push({ frag: slice[i], text, start, end });
        cursor = end;
    }
    /** 目标区间终点。 */
    const rangeEnd = rangeStart + rangeLength;
    /** 累积外包。 */
    let union: { x: number; y: number; w: number; h: number } | null = null;
    for (const seg of segments) {
        if (seg.end <= rangeStart || seg.start >= rangeEnd) {
            continue;
        }
        /** 落在本碎片内的局部起点。 */
        const localStart = Math.max(rangeStart, seg.start) - seg.start;
        /** 落在本碎片内的局部终点。 */
        const localEnd = Math.min(rangeEnd, seg.end) - seg.start;
        /** 本段对目标的贡献框。 */
        let part: { x: number; y: number; w: number; h: number };
        if (localStart === 0 && localEnd === seg.text.length) {
            part = {
                x: seg.frag.x,
                y: seg.frag.y,
                w: seg.frag.w,
                h: seg.frag.h,
            };
        } else {
            /** 局部子串（用于比例宽）。 */
            const localTarget = seg.text.slice(localStart, localEnd);
            part = ratioBoxInFragment(seg.frag, localTarget, localStart);
        }
        union = union ? unionBBox(union, part) : part;
    }
    return union;
}

/**
 * 拼接串是否以目标为前缀，且其后为空或空白词界（允许 CAP 等尾巴）。
 * @param joined 规范化拼接串
 * @param target 规范化目标
 */
function isPrefixWithWordBoundary(joined: string, target: string): boolean {
    if (!joined.startsWith(target)) {
        return false;
    }
    if (joined.length === target.length) {
        return true;
    }
    return joined.charAt(target.length) === ' ';
}

/**
 * 从碎片列表收集与识别值相关的全部候选（单碎片 + 同页连续拼接 + 短码词界）。
 * @param fragments 全页抽字
 * @param value 识别值
 */
export function collectPdfValueSnapCandidates(
    fragments: PdfTextFragment[],
    value: string,
): PdfSnapCandidate[] {
    /** 规范化后的目标串。 */
    const target = normalizePdfSnapText(value);
    if (!target || !fragments.length) {
        return [];
    }
    /** 是否允许包含/拼接匹配。 */
    const allowFuzzy = target.length >= 4;
    /** 输出候选。 */
    const candidates: PdfSnapCandidate[] = [];

    for (const fragment of fragments) {
        /** 碎片规范化文本。 */
        const text = normalizePdfSnapText(fragment.text);
        if (!text) {
            continue;
        }
        if (text === target) {
            candidates.push({
                page: fragment.page,
                bbox: { x: fragment.x, y: fragment.y, w: fragment.w, h: fragment.h },
                exact: true,
                haystackLength: text.length,
            });
            continue;
        }
        if (allowFuzzy && text.indexOf(target) >= 0) {
            /** 子串框：避免整段长文当高亮（页顶说明等）。 */
            const nestedBox = tokenBoxInFragment(fragment, target)
                || ratioBoxInFragment(fragment, target, text.indexOf(target));
            candidates.push({
                page: fragment.page,
                bbox: nestedBox,
                exact: false,
                haystackLength: text.length,
            });
            continue;
        }
        // 短码：在「(VE)」或词界中定位，避免整段精确找不到
        if (!allowFuzzy) {
            const tokenBox = tokenBoxInFragment(fragment, target);
            if (tokenBox) {
                candidates.push({
                    page: fragment.page,
                    bbox: tokenBox,
                    exact: false,
                    haystackLength: target.length,
                });
            }
        }
    }

    if (!allowFuzzy) {
        return candidates;
    }

    // 同页连续碎片拼接（最多向后看 10 段），覆盖「VIALE」+「STAZIONE 4」及 Visura「…STAZIONE 4 CAP…」前缀
    for (let i = 0; i < fragments.length; i += 1) {
        /** 参与拼接的连续碎片。 */
        const slice: PdfTextFragment[] = [fragments[i]];
        /** 累积规范化文本。 */
        let joined = normalizePdfSnapText(fragments[i].text);
        if (!joined) {
            continue;
        }
        for (let k = i + 1; k < fragments.length && k <= i + 10; k += 1) {
            if (fragments[k].page !== fragments[i].page) {
                break;
            }
            slice.push(fragments[k]);
            joined = normalizePdfSnapText(`${joined} ${fragments[k].text}`);
            if (joined === target) {
                /** 精确整句：用全部参与碎片的外包。 */
                const exactBox = boxForNormalizedJoinRange(slice, 0, target.length);
                if (exactBox) {
                    candidates.push({
                        page: fragments[i].page,
                        bbox: exactBox,
                        exact: true,
                        haystackLength: joined.length,
                    });
                }
                continue;
            }
            // 前缀+词界：目标完整出现在拼接串开头，尾巴（如 CAP）不进框；禁止中间包含以免页顶吞字
            if (isPrefixWithWordBoundary(joined, target)) {
                /** 仅目标字符区间的框。 */
                const prefixBox = boxForNormalizedJoinRange(slice, 0, target.length);
                if (prefixBox) {
                    candidates.push({
                        page: fragments[i].page,
                        bbox: prefixBox,
                        exact: true,
                        haystackLength: joined.length,
                    });
                }
            }
        }
    }

    return candidates;
}

/**
 * 多命中消歧：同页 → 精确 → 距后端 bbox 更近 → 特异性高。
 * 有后端粗框时优先贴粗框，避免同词多处（如地址 Madrid vs 日期 En Madrid）选错。
 * @param candidates 全部候选
 * @param hint 后端页码与大致 bbox
 * @param targetLength 目标串长度（算特异性）
 */
export function pickBestPdfSnapCandidate(
    candidates: PdfSnapCandidate[],
    hint: PdfSnapHint,
    targetLength = 1,
    valueForSizeCheck = '',
): PdfSnapCandidate | null {
    if (!candidates.length) {
        return null;
    }
    /** 先丢掉相对识别值过大的框，再排序。 */
    const plausible = valueForSizeCheck
        ? candidates.filter((item) => !isImplausiblePdfSnapBox(item.bbox, valueForSizeCheck))
        : candidates;
    /** 可排序副本；若全被尺寸过滤则回退原列表（再由调用方决定是否采用）。 */
    const ranked = [...(plausible.length ? plausible : candidates)];
    /** 后端框中心；有粗框时作为主消歧依据。 */
    const hintCenter = hint.bbox ? boxCenter(hint.bbox) : null;
    ranked.sort((left, right) => {
        /** 同页优先。 */
        const leftSamePage = left.page === hint.page ? 0 : 1;
        const rightSamePage = right.page === hint.page ? 0 : 1;
        if (leftSamePage !== rightSamePage) {
            return leftSamePage - rightSamePage;
        }
        /** 精确匹配优先。 */
        const leftExact = left.exact ? 0 : 1;
        const rightExact = right.exact ? 0 : 1;
        if (leftExact !== rightExact) {
            return leftExact - rightExact;
        }
        // 有后端 bbox：优先离粗框更近的候选
        if (hintCenter) {
            const leftDist = centerDistanceSq(boxCenter(left.bbox), hintCenter);
            const rightDist = centerDistanceSq(boxCenter(right.bbox), hintCenter);
            if (leftDist !== rightDist) {
                return leftDist - rightDist;
            }
        }
        /** 距离相同或无粗框时：特异性高者优先（更短、更贴目标的 haystack）。 */
        const leftSpec = pdfSnapSpecificity(targetLength, left.haystackLength);
        const rightSpec = pdfSnapSpecificity(targetLength, right.haystackLength);
        if (leftSpec !== rightSpec) {
            return rightSpec - leftSpec;
        }
        return 0;
    });
    /** 最优候选。 */
    const best = ranked[0] || null;
    if (best && valueForSizeCheck && isImplausiblePdfSnapBox(best.bbox, valueForSizeCheck)) {
        return null;
    }
    return best;
}

/**
 * 按识别值在文字层吸附最优框；找不到返回 null（调用方回退后端 bbox）。
 * @param fragments 全页抽字
 * @param value 识别值
 * @param hint 后端页码与大致 bbox
 */
export function findBestPdfValueSnap(
    fragments: PdfTextFragment[],
    value: string,
    hint: PdfSnapHint,
): PdfSnappedBox | null {
    /** 规范化目标。 */
    const target = normalizePdfSnapText(value);
    /** 全部候选。 */
    const candidates = collectPdfValueSnapCandidates(fragments, value);
    /** 消歧后的最优（过大框会被剔除）。 */
    const best = pickBestPdfSnapCandidate(candidates, hint, target.length, value);
    if (!best) {
        return null;
    }
    return { page: best.page, bbox: best.bbox };
}

/**
 * 字段最终用于叠框的页码与矩形（吸附优先，否则后端 bbox）。
 */
export interface ResolvedOcrDisplayBox {
    /** 餐厅字段名。 */
    fieldId: string;
    /** 展示页码。 */
    page: number;
    /** 展示矩形：PDF 为页内 pt；图片为相对原图 0–1（由 DocumentPane 按介质分别换算）。 */
    bbox: { x: number; y: number; w: number; h: number };
}

/** 解析用字段最小结构。 */
type ResolveFieldInput = {
    /** 字段名。 */
    fieldId: string;
    /** 识别值。 */
    valueOcr: string;
    /** 后端页码。 */
    page: number;
    /** 后端框。 */
    bbox: { x: number; y: number; w: number; h: number } | null;
};

/**
 * 同页同值的多个命中框，按中心 y 去重排序。
 * 只要存在精确命中，就只用精确框，避免模糊/拼接外包框参与分行配对。
 * @param fragments 文字层
 * @param value 识别值
 * @param page 页码
 */
function uniquePageBoxesForValue(
    fragments: PdfTextFragment[],
    value: string,
    page: number,
): PdfSnapCandidate[] {
    /** 该页全部候选。 */
    const pageCandidates = collectPdfValueSnapCandidates(fragments, value)
        .filter((item) => item.page === page)
        .filter((item) => item.exact || !isImplausiblePdfSnapBox(item.bbox, value));
    /** 精确命中列表。 */
    const exactCandidates = pageCandidates.filter((item) => item.exact);
    /** 有精确框则只用精确框；否则才退回模糊候选。 */
    const source = exactCandidates.length > 0 ? exactCandidates : pageCandidates;
    /** 按中心 y / x 排序。 */
    const sorted = [...source].sort((a, b) => {
        const ac = boxCenter(a.bbox);
        const bc = boxCenter(b.bbox);
        return ac.y - bc.y || ac.x - bc.x;
    });
    /** 去重结果。 */
    const uniqueBoxes: PdfSnapCandidate[] = [];
    for (const candidate of sorted) {
        const dup = uniqueBoxes.some((item) => (
            centerDistanceSq(boxCenter(item.bbox), boxCenter(candidate.bbox)) < 4
        ));
        if (!dup) {
            uniqueBoxes.push(candidate);
        }
    }
    return uniqueBoxes;
}

/**
 * 为核对字段解析展示用高亮框。
 * 同页同值多字段（税号/增值税号）按后端 bbox.y 与命中 y 一一配对。
 * @param fields 核对字段
 * @param fragments PDF 文字层；空则仅用后端 bbox
 */
export function resolveOcrDisplayBoxes(
    fields: ReadonlyArray<ResolveFieldInput>,
    fragments: PdfTextFragment[],
): ResolvedOcrDisplayBox[] {
    /** 结果列表。 */
    const resolved: ResolvedOcrDisplayBox[] = [];
    /** 已按「值+页」成组处理过的字段。 */
    const assignedIds = new Set<string>();

    if (fragments.length) {
        /** 已处理的分组键。 */
        const seenGroups = new Set<string>();
        for (const field of fields) {
            /** 规范化值。 */
            const valueKey = normalizePdfSnapText(field.valueOcr);
            if (!valueKey) {
                continue;
            }
            /** 分组键。 */
            const groupKey = `${valueKey}#${field.page}`;
            if (seenGroups.has(groupKey)) {
                continue;
            }
            seenGroups.add(groupKey);

            /** 同值同页字段，按后端框自上而下。 */
            const groupFields = fields
                .filter((item) => (
                    normalizePdfSnapText(item.valueOcr) === valueKey
                    && item.page === field.page
                ))
                .sort((a, b) => (a.bbox?.y || 0) - (b.bbox?.y || 0));
            /** 该页去重后的命中框。 */
            const uniqueBoxes = uniquePageBoxesForValue(fragments, field.valueOcr, field.page);

            if (groupFields.length >= 2 && uniqueBoxes.length >= 2) {
                groupFields.forEach((item, order) => {
                    const box = uniqueBoxes[Math.min(order, uniqueBoxes.length - 1)];
                    resolved.push({
                        fieldId: item.fieldId,
                        page: box.page,
                        bbox: box.bbox,
                    });
                    assignedIds.add(item.fieldId);
                });
            }
        }
    }

    for (const field of fields) {
        if (assignedIds.has(field.fieldId)) {
            continue;
        }
        /** 文字层吸附结果。 */
        const snapped = fragments.length
            ? findBestPdfValueSnap(fragments, field.valueOcr, {
                page: field.page,
                bbox: field.bbox,
            })
            : null;
        if (snapped) {
            resolved.push({
                fieldId: field.fieldId,
                page: snapped.page,
                bbox: snapped.bbox,
            });
            continue;
        }
        if (field.bbox) {
            resolved.push({
                fieldId: field.fieldId,
                page: field.page,
                bbox: field.bbox,
            });
        }
    }
    return resolved;
}

/**
 * pdf.js viewport 最小接口：把用户空间矩形转到与画布一致的左上坐标系。
 * 必须用 convertToViewportRectangle，才能扣掉 crop/view 边距（如 Agreement PDF 的 [10,10,...]）。
 */
export interface PdfJsViewportLike {
    /** 可视区高度（scale=1 时通常为 view 高）。 */
    height: number;
    /**
     * 将 PDF 用户空间矩形 [x1,y1,x2,y2] 转为 viewport 坐标。
     * @param rect 用户空间矩形
     */
    convertToViewportRectangle: (rect: number[]) => number[];
}

/**
 * 从 pdf.js textContent 单项转成左上原点碎片（与渲染画布同一坐标系）。
 * @param item pdf.js text item
 * @param page 页码
 * @param viewport scale=1 的页 viewport（含 crop 偏移）
 */
export function mapPdfJsTextItemToFragment(
    item: {
        str?: string;
        width?: number;
        height?: number;
        transform?: number[];
    },
    page: number,
    viewport: PdfJsViewportLike,
): PdfTextFragment | null {
    /** 原文。 */
    const text = `${item.str || ''}`.replace(/\s+/g, ' ').trim();
    if (!text || !item.transform || item.transform.length < 6) {
        return null;
    }
    /** 字号近似高度。 */
    const h = Math.abs(item.height || 0) || Math.abs(item.transform[3] || 0) || 10;
    /** 宽度；缺省按字数估算。 */
    const w = item.width && item.width > 0
        ? item.width
        : Math.max(h * 0.5 * text.length, h * 0.4);
    /** PDF 用户空间基线左下 x。 */
    const xPdf = item.transform[4];
    /** PDF 用户空间基线 y（底起）。 */
    const yPdf = item.transform[5];
    /** 用户空间字框 → viewport 左上矩形（自动扣 crop 边距）。 */
    const rect = viewport.convertToViewportRectangle([xPdf, yPdf, xPdf + w, yPdf + h]);
    /** viewport 左缘。 */
    const x = Math.min(rect[0], rect[2]);
    /** viewport 顶缘。 */
    const y = Math.min(rect[1], rect[3]);
    /** viewport 宽度。 */
    const width = Math.abs(rect[2] - rect[0]);
    /** viewport 高度。 */
    const height = Math.abs(rect[3] - rect[1]);
    return { page, text, x, y, w: width, h: height };
}

